/**
 * More Colours Component
 * Displays a popup with related products based on vendor
 */

class MoreColoursComponent extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.productCache = new Map();
  }

  connectedCallback() {
    this.vendor = this.dataset.vendor;
    this.currentProductHandle = this.dataset.currentProduct;
    this.context = this.dataset.context || 'product-card'; // 'product-card' or 'product-page'
    this.currentSlide = 0;
    this.totalProducts = 0;
    this.resizeHandler = null;
    
    if (!this.vendor) {
      this.style.display = 'none';
      return;
    }

    this.createDialog();
    this.attachEventListeners();
    
    // Defer the vendor lookup until the card is near the viewport, so bulk-inserting
    // many cards (pagination / restore) doesn't fire a burst of searches that 429.
    this.observeForAvailability();
  }

  observeForAvailability() {
    // Observe a visible ancestor — this element itself starts display:none, and an
    // IntersectionObserver never fires on a display:none target.
    const anchor = this.closest('.product-grid__item, [data-product-id]') || this.parentElement || this;
    this.availabilityObserver = new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) {
        obs.disconnect();
        this.availabilityObserver = null;
        this.checkProductAvailability();
      }
    }, { rootMargin: '200px' });
    this.availabilityObserver.observe(anchor);
  }

  async checkProductAvailability() {
    try {
      const searchQuery = `vendor:${encodeURIComponent(this.vendor)}`;
      const response = await fetch(`/search?view=vendor-products&type=product&q=${searchQuery}`);
      const products = await response.json();
      
      // Filter out the current product
      const filteredProducts = products.filter((p) => p.handle !== this.currentProductHandle);
      
      // Show component if there are other colors available, otherwise keep hidden
      if (filteredProducts.length > 0) {
        this.style.display = '';
      } else {
        this.style.display = 'none';
      }
      
      // Cache the products for later use
      this.productCache.set(this.vendor, filteredProducts);
    } catch (error) {
      console.error('Error checking product availability:', error);
      // Keep component hidden on error
      this.style.display = 'none';
    }
  }

  disconnectedCallback() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    this.availabilityObserver?.disconnect();
  }

  getProductsPerSlide() {
    // Desktop: 2 products per slide, Mobile: 1 product per slide
    return window.innerWidth >= 750 ? 2 : 1;
  }

  getTotalSlides() {
    const productsPerSlide = this.getProductsPerSlide();
    return Math.ceil(this.totalProducts / productsPerSlide);
  }

  createDialog() {
    // Create dialog element
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'more-colours-dialog';
    this.dialog.innerHTML = `
      <div class="more-colours-dialog__content">
        <div class="more-colours-dialog__header">
          <h3 class="more-colours-dialog__title">More Colours</h3>
          <button class="more-colours-dialog__close" type="button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="more-colours-dialog__loading">Loading...</div>
        <div class="more-colours-dialog__products"></div>
      </div>
    `;

    this.appendChild(this.dialog);

    // Add event listeners for dialog
    const closeButton = this.dialog.querySelector('.more-colours-dialog__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeDialog();
      });
    }

    // Close on backdrop click
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.closeDialog();
      }
    });

    // Close on escape key
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDialog();
      }
    });
  }

  attachEventListeners() {
    const trigger = this.querySelector('[data-more-colours-trigger]');
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDialog();
      });
    }
  }

  async openDialog() {
    if (!this.dialog) return;
    
    this.dialog.showModal();
    
    // Load products if not already cached
    if (!this.productCache.has(this.vendor)) {
      await this.fetchProducts();
    } else {
      this.renderProducts(this.productCache.get(this.vendor));
    }
  }

  closeDialog() {
    if (!this.dialog) return;
    this.dialog.close();
  }

  async fetchProducts() {
    if (!this.dialog || !this.vendor) return;
    
    const loadingEl = this.dialog.querySelector('.more-colours-dialog__loading');
    if (loadingEl) {
      loadingEl.style.display = 'block';
    }

    try {
      // Use Shopify search to find products by vendor
      const searchQuery = `vendor:${encodeURIComponent(this.vendor)}`;
      const response = await fetch(`/search?view=vendor-products&type=product&q=${searchQuery}`);
      const products = await response.json();
      
      // Filter out the current product
      const filteredProducts = products.filter((p) => p.handle !== this.currentProductHandle);
      
      this.productCache.set(this.vendor, filteredProducts);
      this.renderProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      const productsEl = this.dialog.querySelector('.more-colours-dialog__products');
      if (productsEl) {
        productsEl.innerHTML = '<p class="more-colours-dialog__error">Error loading products. Please try again.</p>';
      }
    } finally {
      if (loadingEl) {
        loadingEl.style.display = 'none';
      }
    }
  }

  renderProducts(products) {
    if (!this.dialog) return;
    
    const productsContainer = this.dialog.querySelector('.more-colours-dialog__products');
    if (!productsContainer) return;
    
    if (!products || products.length === 0) {
      productsContainer.innerHTML = '<p class="more-colours-dialog__empty">No other colours available.</p>';
      return;
    }

    // Show all products in a slider
    const displayProducts = products;
    this.totalProducts = displayProducts.length;
    
    const productsHTML = displayProducts.map((product) => this.renderProductCard(product)).join('');
    
    productsContainer.innerHTML = `
      <div class="more-colours-slider-wrapper">
        <div class="more-colours-dialog__slider" data-main-slider>
          <div class="more-colours-dialog__slider-track" data-slider-track>
            ${productsHTML}
          </div>
        </div>
      </div>
      <div class="more-colours-slider-controls">
        <button class="more-colours-slider-nav more-colours-slider-nav--prev" data-main-slider-prev aria-label="Previous product">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18L9 12L15 6"/>
          </svg>
        </button>
        <div class="more-colours-slider-pagination" data-slider-pagination></div>
        <button class="more-colours-slider-nav more-colours-slider-nav--next" data-main-slider-next aria-label="Next product">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 6L15 12L9 18"/>
          </svg>
        </button>
      </div>
    `;
    
    this.initMainSlider();
    this.updatePagination();
    this.initProductImageSliders();
    this.updateArrowsVisibility();
  }

  initProductImageSliders() {
    if (!this.dialog) return;
    
    const productSliders = this.dialog.querySelectorAll('[data-product-image-slider]');
    
    productSliders.forEach(slider => {
      const sliderId = slider.getAttribute('data-product-image-slider');
      const prevBtn = slider.querySelector(`[data-product-slider-prev="${sliderId}"]`);
      const nextBtn = slider.querySelector(`[data-product-slider-next="${sliderId}"]`);
      const dots = slider.querySelectorAll(`[data-product-slider-id="${sliderId}"]`);
      const slides = slider.querySelectorAll('.more-colours-slider__slide');
      
      let currentIndex = 0;
      
      const updateSlides = (newIndex) => {
        currentIndex = newIndex;
        slides.forEach((slide, idx) => {
          slide.classList.toggle('active', idx === currentIndex);
        });
        dots.forEach((dot, idx) => {
          dot.classList.toggle('active', idx === currentIndex);
        });
      };
      
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const newIndex = (currentIndex - 1 + slides.length) % slides.length;
          updateSlides(newIndex);
        });
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const newIndex = (currentIndex + 1) % slides.length;
          updateSlides(newIndex);
        });
      }
      
      dots.forEach((dot, idx) => {
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          updateSlides(idx);
        });
      });
    });
  }

  renderProductCard(product) {
    const mediaSlider = product.media.length > 1 
      ? this.renderMediaSlider(product)
      : this.renderSingleImage(product);

    return `
      <div class="more-colours-product-card" data-product-slide>
        <a href="${product.url}" class="more-colours-product-card__link">
          ${mediaSlider}
          <div class="more-colours-product-card__info">
            <h4 class="more-colours-product-card__title">${product.title}</h4>
            <div class="more-colours-product-card__price">
              ${this.renderPrice(product)}
            </div>
          </div>
        </a>
      </div>
    `;
  }

  renderSingleImage(product) {
    return `
      <div class="more-colours-product-card__image">
        <img src="${product.featured_image || ''}" alt="${product.title}" loading="lazy">
      </div>
    `;
  }

  renderMediaSlider(product) {
    const slides = product.media.map((media, index) => `
      <div class="more-colours-slider__slide ${index === 0 ? 'active' : ''}">
        <img src="${media.preview_image}" alt="${product.title}" loading="lazy">
      </div>
    `).join('');

    const sliderId = `slider-${Math.random().toString(36).substr(2, 9)}`;

    return `
      <div class="more-colours-product-card__slider" data-product-image-slider="${sliderId}">
        <div class="more-colours-slider__container" data-slider-container>
          ${slides}
        </div>
        ${product.media.length > 1 ? `
          <button class="more-colours-slider__nav more-colours-slider__nav--prev" data-product-slider-prev="${sliderId}" aria-label="Previous image">
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.75 10.75L0.75 5.75L5.75 0.75" stroke="#222222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="more-colours-slider__nav more-colours-slider__nav--next" data-product-slider-next="${sliderId}" aria-label="Next image">
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 0.75L5.75 5.75L0.75 10.75" stroke="#222222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="more-colours-slider__dots">
            ${product.media.map((_, index) => `
              <button class="more-colours-slider__dot ${index === 0 ? 'active' : ''}" data-product-slide-index="${index}" data-product-slider-id="${sliderId}" aria-label="Go to image ${index + 1}"></button>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderPrice(product) {
    const price = (product.price / 100).toFixed(2);
    const comparePrice = product.compare_at_price ? (product.compare_at_price / 100).toFixed(2) : null;

    if (comparePrice && comparePrice > price) {
      return `
        <span class="more-colours-product-card__price--sale">$${price}</span>
        <span class="more-colours-product-card__price--compare">$${comparePrice}</span>
      `;
    }
    
    return `<span>$${price}</span>`;
  }

  initMainSlider() {
    if (!this.dialog) return;
    
    const track = this.dialog.querySelector('[data-slider-track]');
    const prevBtn = this.dialog.querySelector('[data-main-slider-prev]');
    const nextBtn = this.dialog.querySelector('[data-main-slider-next]');
    
    if (!track) return;

    // Navigation buttons with stopPropagation
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.goToSlide(this.currentSlide - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.goToSlide(this.currentSlide + 1);
      });
    }

    // Touch/swipe support
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    let isSwiping = false;

    track.addEventListener('touchstart', (e) => {
      // Don't start swipe if touching product image slider controls
      if (e.target.closest('[data-product-slider-prev]') || 
          e.target.closest('[data-product-slider-next]') ||
          e.target.closest('[data-product-slide-index]')) {
        return;
      }
      
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
      isDragging = true;
      isSwiping = false;
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(e.touches[0].clientY - startY);
      
      // If horizontal movement is greater than vertical, it's a swipe
      if (diffX > 10 && diffX > diffY) {
        isSwiping = true;
      }
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      const diff = startX - currentX;
      
      // If we detected a swipe gesture, prevent link clicks
      if (isSwiping && Math.abs(diff) > 30) {
        // Prevent any click events from firing
        const preventClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          track.removeEventListener('click', preventClick, true);
        };
        track.addEventListener('click', preventClick, true);
        
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            this.goToSlide(this.currentSlide + 1);
          } else {
            this.goToSlide(this.currentSlide - 1);
          }
        }
      }
      
      isDragging = false;
      isSwiping = false;
    });

    // Mouse drag support for desktop
    track.addEventListener('mousedown', (e) => {
      // Don't start drag if clicking on product image slider controls
      if (e.target.closest('[data-product-image-slider]') || 
          e.target.closest('[data-product-slider-prev]') || 
          e.target.closest('[data-product-slider-next]') ||
          e.target.closest('[data-product-slide-index]')) {
        return;
      }
      
      e.preventDefault();
      startX = e.clientX;
      isDragging = true;
      track.style.cursor = 'grabbing';
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      currentX = e.clientX;
    });

    track.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      isDragging = false;
      track.style.cursor = 'grab';
      
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.goToSlide(this.currentSlide + 1);
        } else {
          this.goToSlide(this.currentSlide - 1);
        }
      }
    });

    track.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        track.style.cursor = 'grab';
      }
    });

    // Handle window resize
    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);

    this.updateSlidePosition();
  }

  handleResize() {
    // Recalculate current slide position when switching between mobile/desktop
    const totalSlides = this.getTotalSlides();
    if (this.currentSlide >= totalSlides) {
      this.currentSlide = totalSlides - 1;
    }
    this.updatePagination();
    this.updateSlidePosition();
    this.updateArrowsVisibility();
  }

  updateArrowsVisibility() {
    if (!this.dialog) return;
    
    const controls = this.dialog.querySelector('.more-colours-slider-controls');
    const totalSlides = this.getTotalSlides();
    
    const shouldShow = totalSlides > 1;
    
    if (controls) controls.style.display = shouldShow ? 'flex' : 'none';
  }

  updatePagination() {
    if (!this.dialog) return;
    
    const pagination = this.dialog.querySelector('[data-slider-pagination]');
    if (!pagination) return;
    
    const totalSlides = this.getTotalSlides();
    const dotsHTML = Array.from({ length: totalSlides }, (_, index) => `
      <button class="more-colours-slider-dot ${index === this.currentSlide ? 'active' : ''}" data-slide-to="${index}" aria-label="Go to slide ${index + 1}"></button>
    `).join('');
    
    pagination.innerHTML = dotsHTML;
    
    // Re-attach event listeners to new dots with stopPropagation
    const dots = pagination.querySelectorAll('[data-slide-to]');
    dots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const slideIndex = parseInt(e.target.dataset.slideTo);
        this.goToSlide(slideIndex);
      });
    });
  }

  goToSlide(index) {
    const totalSlides = this.getTotalSlides();
    if (!totalSlides) return;
    
    // Loop around
    if (index < 0) {
      this.currentSlide = totalSlides - 1;
    } else if (index >= totalSlides) {
      this.currentSlide = 0;
    } else {
      this.currentSlide = index;
    }
    
    this.updateSlidePosition();
  }

  updateSlidePosition() {
    if (!this.dialog) return;
    
    const track = this.dialog.querySelector('[data-slider-track]');
    const dots = this.dialog.querySelectorAll('[data-slide-to]');
    
    if (!track) return;

    // Calculate transform based on products per slide
    const productsPerSlide = this.getProductsPerSlide();
    const slideWidth = 100 / productsPerSlide; // Width of one product
    const translateX = this.currentSlide * slideWidth * productsPerSlide;
    
    track.style.transform = `translateX(-${translateX}%)`;
    
    // Update dots
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlide);
    });
  }
}

// Define custom element
if (!customElements.get('more-colours-component')) {
  customElements.define('more-colours-component', MoreColoursComponent);
}
